import { ViewOnlyMode } from '../../base-ui/css/ViewOnlyMode';
import { checkIfHasPermission } from '../../components/user-permissions/HasPermission';
import {
	IDLE_OBSERVER_INTERVAL_IN_SECONDS,
	IDLE_OBSERVER_NOTIFICATION_DURATION_IN_SECONDS,
	IDLE_OBSERVER_NOTIFICATION_MESSAGE,
} from '../../config/Environment';
import type { NavinaLoginStep } from '../../pages/auth/steps/NavinaLoginStepEnum';
import type { TabsViewTabName } from '../../pages/summary/Tabs';
import { useFeatureFlag } from '../../pages/summary/behaviours/useFeatureFlag';
import { useAuthStore } from '../../stores/authStore';
import { getOrCreateIdleObserver } from '../../utils/IdleObserver';
import { getOrCreateAddonCommunicator, OverlayProtocolTypes } from '../../utils/addon/addon-communicator';
import { inIframe, loggedInViaSSo } from '../../utils/common';
import { NEXT_URL_QUERY_PARAMS, SSO_URL_QUERY_PARAMS } from '../../utils/consts';
import { getOrCreateLogger } from '../../utils/logger';
import { useNavinaData } from '../../utils/useNavinaData';
import { notification } from 'antd';
import { observer } from 'mobx-react-lite';
import QueryString from 'query-string';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

interface ProtectedRouteProps {
	readonly isSummaryView?: boolean;
	readonly overlayPageName?: TabsViewTabName;
	readonly defaultStepId?: NavinaLoginStep;
	readonly hideActionButton?: boolean;
}

const EmptyDiv = <div />;

export const ProtectedRoute = observer(function ProtectedRoute({ isSummaryView }: ProtectedRouteProps) {
	const authStore = useAuthStore();
	const navina = useNavinaData();
	const overlayOnlyView = useFeatureFlag<boolean | undefined>('configuration_OverlayOnlyView', undefined);
	const location = useLocation();

	const [loggedIn, setLoggedIn] = useState(authStore?.isLoggedIn);
	let queryString = QueryString.parse(window.location.search);
	const ssoUrl = queryString[SSO_URL_QUERY_PARAMS];

	const idleTimeoutInSecondsOrNull = useFeatureFlag<number | null>('configuration_IdleTimeoutInMilliseconds', null);

	const idleObserver = getOrCreateIdleObserver();
	const isLoggingIn = authStore?.isLoggedIn;

	useEffect(
		function setLoggedInStatus(): void {
			if (authStore) {
				setLoggedIn(isLoggingIn);
			}
		},
		[authStore, isLoggingIn],
	);

	useEffect(
		function initializeIdleObserver(): VoidFunction | undefined {
			if (!isLoggingIn) {
				return;
			}

			const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
			let dispose: VoidFunction | null = null;

			function handleActivityEvent(): void {
				idleObserver
					.setLastActivityTimestamp()
					.catch((error) => console.error('Error setting last activity timestamp:', error));
			}
			function createDisposeFunction(): VoidFunction {
				return function removeEventListeners(): void {
					for (const event of events) {
						globalThis.removeEventListener(event, handleActivityEvent);
					}
				};
			}
			function setupActivityListeners(): void {
				for (const event of events) {
					globalThis.addEventListener(event, handleActivityEvent);
				}
				dispose = createDisposeFunction();
			}
			idleObserver
				.initialize()
				.then(setupActivityListeners)
				.catch(() => console.error('Error initializing idle observer:'));

			return () => dispose?.();
		},
		[isLoggingIn, idleObserver],
	);

	useEffect((): void => {
		if (!idleTimeoutInSecondsOrNull) {
			return;
		}
		idleObserver.setIdleTimeoutInMilliseconds(idleTimeoutInSecondsOrNull);
	}, [idleTimeoutInSecondsOrNull, idleObserver]);

	useEffect(() => {
		if (!isLoggingIn) {
			return;
		}

		const performAction = async () => {
			const isIdle = await idleObserver.isIdle();
			if (!isIdle) {
				return;
			}

			notification.info({
				className: 'ant-notification-with-absolute-close',
				message: IDLE_OBSERVER_NOTIFICATION_MESSAGE,
				placement: 'top',
				duration: IDLE_OBSERVER_NOTIFICATION_DURATION_IN_SECONDS,
				onClose: () => {
					authStore.logoutAndRedirectToLoginPage();
				},
			});
		};

		// Set up the interval to call performAction every INTERVAL_TIME_IN_SECONDS seconds
		const intervalId = setInterval(performAction, IDLE_OBSERVER_INTERVAL_IN_SECONDS * 1000);

		// Cleanup function to clear the interval when the component unmounts
		return () => {
			clearInterval(intervalId);
		};
	}, [isLoggingIn, idleObserver, authStore]);

	let shouldChangeToOverlay = false;

	if (!navina.isOverlayOrEmbedded && !window.location.pathname.includes('/overlay/')) {
		if (overlayOnlyView && isSummaryView) {
			shouldChangeToOverlay = true;
			window.location.pathname = `overlay${window.location.pathname}`;
		}
	}

	if (loggedIn) {
		if (localStorage.getItem('hadError')) {
			localStorage.removeItem('hadError');
		}

		const isReady = !shouldChangeToOverlay;

		if (!isReady) {
			return EmptyDiv;
		}

		const isViewOnly = checkIfHasPermission(authStore, 'view_only', false);

		return (
			<>
				<Outlet />
				{isViewOnly && <ViewOnlyMode />}
			</>
		);
	}

	if (ssoUrl && typeof ssoUrl === 'string' && !loggedInViaSSo()) {
		const { [SSO_URL_QUERY_PARAMS]: _removedParam, ...restQueryString } = queryString;
		queryString = restQueryString;

		const nextUrl = `${window.location.pathname}?${QueryString.stringify(queryString)}`;

		getOrCreateLogger().info('starting sso process', { ssoUrl, nextUrl });
		localStorage.setItem(NEXT_URL_QUERY_PARAMS, nextUrl);
		localStorage.setItem('ssoUrl', ssoUrl);

		const isInIframe = inIframe();

		if (isInIframe) {
			getOrCreateAddonCommunicator().sendToAddon(OverlayProtocolTypes['addon-change-frame-src'], { url: ssoUrl });
		} else {
			window.location.href = ssoUrl;
			return null;
		}
	}

	const nextUrl = `/login${location.pathname}${location.search}`;
	getOrCreateLogger().info('ProtectedRoute: redirecting unauthenticated user to login', {
		fromPath: location.pathname,
		fromHasQuery: location.search.length > 0,
		toPath: `/login${location.pathname}`,
	});
	return <Navigate to={nextUrl} />;
});
